/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import React, { forwardRef, useState } from "react";
import * as RPNInput from "react-phone-number-input";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPercentages } from "../../../../../global/emotion";
import { TextInput } from "../TextInput/TextInput.emotion";
import { PhoneCountryFlag } from "./PhoneCountryFlag.emotion";
import { PhoneCountrySelect } from "./PhoneCountrySelect.emotion";

const baseStyle: Interpolation = {
  display: "flex",
  justifyItems: "stretch",
  width: numericPercentages[100],
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  borderBottomLeftRadius: "0px",
  borderLeftColor: "transparent",
  borderTopLeftRadius: "0px",
  flexGrow: 1,
  ":hover:not(:disabled)": {
    borderLeftColor: theme.semanticColors.neutralsVeryLow,
  },
  ":focus": {
    borderLeftColor: `${theme.semanticColors.primary} !important`,
  },
});

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> & {
  locale?: string;
  required?: boolean;
  inputComponent?: React.ElementType<any, keyof React.JSX.IntrinsicElements>;
  countrySelectComponent?: React.ElementType<
    any,
    keyof React.JSX.IntrinsicElements
  >;
} & Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
  };

export const PhoneInput = ({
  name,
  locale,
  required = false,
  onChange,
  inputComponent,
  countrySelectComponent,
  ...props
}: PhoneInputProps) => {
  const [country, setCountry] = useState<RPNInput.Country>("US");

  return (
    <RPNInput.default
      css={baseStyle}
      international
      autoComplete="tel"
      autoFocus
      defaultCountry={country}
      flagComponent={PhoneCountryFlag}
      countrySelectComponent={countrySelectComponent ?? PhoneCountrySelect}
      inputComponent={inputComponent ?? InputComponent}
      onChange={(value) => onChange?.(value)}
      onCountryChange={(country) => setCountry(country)}
      name={name}
      {...props}
    />
  );
};

PhoneInput.displayName = "PhoneInput";

const InputComponent = forwardRef<HTMLInputElement>((props, ref) => {
  return <TextInput css={inputStyle} {...props} ref={ref} />;
});
